import '../../common/bn-form-field';
import '../../common/bn-icon';
import '../../common/bn-map';
import '../../common/bn-map-marker';
import { entityStore } from '~/utility/BinSentry-ui-utility';
import { LitEntity, SirenActionMixin, html, css, renderIf } from '../../utility/lit-entity';
import { sharedStyles } from '../../styles/shared-styles';
import { bannerStyles } from '../../styles/banner-styles';

class WorkOrderBinLocation extends SirenActionMixin(LitEntity) {
  constructor() {
    super();
    this.longitude = 0.0;
    this.latitude = 0.0;
  }

  static get properties() {
    return {
      longitude: { type: Number, attribute: false },
      latitude: { type: Number, attribute: false },
      showMap: { type: Boolean, attribute: 'show-map' },
      googleMapsKey: { type: String, attribute: 'google-maps-key' },
    };
  }

  static get styles() {
    return [sharedStyles, bannerStyles, css`
      :host {
        display: block;
      }
      bn-map {
        height: 300px;
      }
      .actions {
        margin-top: 1em;
      }
      .actions .button {
        margin: 8px 0 0;
      }
    `];
  }

  render() {
    return html`
      <p class="description subdued">
        Make sure the pin is as close as possible to the actual bin.
      </p>

      <bn-form-field
        type="number"
        name="latitude"
        title="Latitude"
        .value="${this.latitude}"
        @input="${(e) => this.latitude = e.target.input.value}"
      ></bn-form-field>

      <bn-form-field
        type="number"
        name="longitude"
        title="Longitude"
        .value="${this.longitude}"
        @input="${(e) => this.longitude = e.target.input.value}"
      ></bn-form-field>

      ${renderIf(this.showMap, () => html`
        <bn-map id="map" zoom="18" .googleMapsKey="${this.googleMapsKey}">
          <bn-map-marker
            .latitude="${this.latitude}"
            .longitude="${this.longitude}"
            draggable
            @map-marker-move="${this._onMarkerMove}"
            @map-marker-dragend="${this._onMarkerDragEnd}"
          >
          </bn-map-marker>
        </bn-map>
      `)}

      <bn-flash></bn-flash>

      <div class="actions">
        <button class="button button--outlined" @click="${this._currentLocation}">Current Location</button>
        <button class="button button--contained" @click="${this._submit}">Save Location</button>
      </div>
    `;
  }

  _onMarkerMove() {
    this.shadowRoot.querySelector('#map').fitMarkers();
  }

  _onMarkerDragEnd(e) {
    const marker = e.target;
    this.latitude = marker.latitude;
    this.longitude = marker.longitude;
    this.shadowRoot.querySelector('#map').fitMarkers();
  }

  _currentLocation() {
    const flash = this.shadowRoot.querySelector('bn-flash');
    flash.clear();
    flash.info(html`
      <bn-icon name="far fa-spinner fa-pulse">⚙</bn-icon>&nbsp;
      Attempting to get your current location.
    `);

    this.accuracy = Number.MAX_SAFE_INTEGER;

    let watchId;
    let timeoutId;

    const options = {
      enableHighAccuracy: true,
      maximumAge: 30000,
      timeout: 29000,
    };

    const done = (err) => {
      navigator.geolocation.clearWatch(watchId);
      clearTimeout(timeoutId);
      flash.clear();
      err && flash.critical(err.message);
    };

    const updatePosition = (position) => {
      if (position.coords.accuracy > this.accuracy) {
        return;
      }

      this.accuracy = position.coords.accuracy;
      this.latitude = position.coords.latitude;
      this.longitude = position.coords.longitude;

      // if accuracy is less than 10m, we can stop checking
      if (this.accuracy <= 10) {
        done();
      }
    };

    watchId = navigator.geolocation.watchPosition(updatePosition, done, options);
    timeoutId = setTimeout(done, options.timeout);
  }

  _entityChanged(entity) {
    super._entityChanged(entity);
    const action = this.entity.getActionByName('update-location');
    this.latitude = action.getFieldByName('location[latitude]').value;
    this.longitude = action.getFieldByName('location[longitude]').value;
  }

  async _submit() {
    const flash = this.shadowRoot.querySelector('bn-flash');
    flash.clear();
    const action = this.entity.getActionByName('update-location');
    action.getFieldByName('location[latitude]').value = this.latitude;
    action.getFieldByName('location[longitude]').value = this.longitude;
    const fields = this.getActionFields(action);
    try {
      const { entity, error } = await this.submitAction(action, fields);
      if (error) {
        throw new Error(error);
      }
      entityStore.invalidateHref(this.href);
      flash.success(this.successtext || 'Success.');
      this.dispatchEvent(new CustomEvent('response', { detail: { entity }, bubbles: true, composed: true }));
    } catch (error) {
      flash.critical(html`${this.errortext}<div><code>${error.message}</code></div>`);
    }
  }
}

window.customElements.define('work-order-bin-location', WorkOrderBinLocation);
